import * as tf from '@tensorflow/tfjs';
import { RGBtoHSV } from './colorUtils';
import { type SeasonUndertone } from './seasonUndertone';

export interface SkinToneResult {
  category: string;
  confidence: number;
  color: string; // Hex color value for the skin tone
  undertone?: SeasonUndertone;
  analysisPoints?: number;
}

// Enhanced skin tone color mappings with more granular categories
const SKIN_TONE_COLORS: Record<string, string> = {
  'Very Fair': '#FFF5E6',
  Fair: '#FFE4C4',
  Light: '#F5DEB3',
  Medium: '#E6BE8A',
  'Medium-Tan': '#D2B48C',
  Olive: '#BDB76B',
  Tan: '#D2B48C',
  Brown: '#A0522D',
  Dark: '#8B4513',
  'Very Dark': '#654321',
  Unknown: '#808080',
};

class TensorFlowService {
  private isInitialized: boolean = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      await tf.ready();
      this.isInitialized = true;
    } catch {
      // Still mark as initialized for basic functionality
      this.isInitialized = true;
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Enhanced skin tone analysis using three independent algorithms
   * combined for better accuracy.
   */
  async analyzeSkinTone(
    pixels: Uint8ClampedArray,
    _imageWidth?: number,
    _imageHeight?: number
  ): Promise<SkinToneResult> {
    if (!this.isInitialized) {
      throw new Error('TensorFlow not initialized');
    }

    try {
      if (!pixels || pixels.length === 0) {
        return this.getDefaultSkinToneResult();
      }

      const results = await Promise.all([
        this.analyzeSkinToneHSV(pixels),
        this.analyzeSkinToneRGB(pixels),
        this.analyzeSkinToneLab(pixels),
      ]);

      return this.combineAnalysisResults(results);
    } catch {
      return this.getDefaultSkinToneResult();
    }
  }

  /**
   * HSV-based skin tone analysis.
   *
   * Uses exhaustive brightness-first ranges so every possible pixel value
   * maps to a concrete category — there are no gaps that can produce 'Unknown'.
   * Saturation is used as a secondary discriminator within each brightness band.
   */
  private async analyzeSkinToneHSV(
    pixels: Uint8ClampedArray
  ): Promise<SkinToneResult> {
    let r = 0,
      g = 0,
      b = 0;
    let count = 0;

    for (let i = 0; i < pixels.length; i += 3) {
      r += pixels[i];
      g += pixels[i + 1];
      b += pixels[i + 2];
      count++;
    }

    const avgR = Math.round(r / count);
    const avgG = Math.round(g / count);
    const avgB = Math.round(b / count);

    const hsv = RGBtoHSV(avgR, avgG, avgB);

    let category: string;
    let confidence: number;

    if (hsv.v > 0.88) {
      category = 'Very Fair';
      confidence = 0.92;
    } else if (hsv.v > 0.74) {
      category = hsv.s <= 0.28 ? 'Fair' : 'Light';
      confidence = 0.88;
    } else if (hsv.v > 0.6) {
      category = hsv.s <= 0.38 ? 'Medium' : 'Medium-Tan';
      confidence = 0.83;
    } else if (hsv.v > 0.46) {
      category = hsv.s <= 0.38 ? 'Olive' : 'Tan';
      confidence = 0.8;
    } else if (hsv.v > 0.3) {
      category = 'Brown';
      confidence = 0.82;
    } else if (hsv.v > 0.15) {
      category = 'Dark';
      confidence = 0.85;
    } else {
      category = 'Very Dark';
      confidence = 0.9;
    }

    return {
      category,
      confidence,
      color: SKIN_TONE_COLORS[category] ?? SKIN_TONE_COLORS['Unknown'],
    };
  }

  /**
   * RGB-based skin tone analysis.
   * Includes a final fallback so every pixel maps to a category.
   */
  private async analyzeSkinToneRGB(
    pixels: Uint8ClampedArray
  ): Promise<SkinToneResult> {
    let r = 0,
      g = 0,
      b = 0;
    let count = 0;

    for (let i = 0; i < pixels.length; i += 3) {
      r += pixels[i];
      g += pixels[i + 1];
      b += pixels[i + 2];
      count++;
    }

    const avgR = r / count;
    const avgG = g / count;
    const avgB = b / count;

    const total = avgR + avgG + avgB;
    const redDominance = total > 0 ? avgR / total : 0;
    const blueDominance = total > 0 ? avgB / total : 0;

    let category: string;
    let confidence: number;

    if (redDominance > 0.4 && blueDominance < 0.25) {
      category = avgR > 220 ? 'Very Fair' : 'Fair';
      confidence = 0.85;
    } else if (redDominance > 0.35 && blueDominance < 0.3) {
      category = avgR > 200 ? 'Light' : 'Medium';
      confidence = 0.8;
    } else if (redDominance > 0.3 && blueDominance < 0.35) {
      category = avgR > 180 ? 'Medium-Tan' : 'Olive';
      confidence = 0.75;
    } else if (redDominance > 0.25 && blueDominance < 0.4) {
      category = avgR > 150 ? 'Tan' : 'Brown';
      confidence = 0.8;
    } else if (redDominance > 0.2) {
      category = avgR > 100 ? 'Dark' : 'Very Dark';
      confidence = 0.85;
    } else {
      // Very low red dominance (e.g. strongly blue-tinted frame)
      category = 'Very Dark';
      confidence = 0.65;
    }

    return {
      category,
      confidence,
      color: SKIN_TONE_COLORS[category] ?? SKIN_TONE_COLORS['Unknown'],
    };
  }

  /**
   * CIE Lab-based skin tone analysis.
   * Lab lightness (L*) is perceptually uniform and naturally exhaustive.
   */
  private async analyzeSkinToneLab(
    pixels: Uint8ClampedArray
  ): Promise<SkinToneResult> {
    let r = 0,
      g = 0,
      b = 0;
    let count = 0;

    for (let i = 0; i < pixels.length; i += 3) {
      r += pixels[i];
      g += pixels[i + 1];
      b += pixels[i + 2];
      count++;
    }

    const lab = this.rgbToLab(r / count, g / count, b / count);

    let category: string;
    let confidence: number;

    if (lab.L > 80) {
      category = 'Very Fair';
      confidence = 0.9;
    } else if (lab.L > 70) {
      category = 'Fair';
      confidence = 0.85;
    } else if (lab.L > 60) {
      category = 'Light';
      confidence = 0.8;
    } else if (lab.L > 50) {
      category = 'Medium';
      confidence = 0.8;
    } else if (lab.L > 40) {
      category = 'Medium-Tan';
      confidence = 0.75;
    } else if (lab.L > 30) {
      category = 'Tan';
      confidence = 0.8;
    } else if (lab.L > 20) {
      category = 'Brown';
      confidence = 0.8;
    } else if (lab.L > 10) {
      category = 'Dark';
      confidence = 0.85;
    } else {
      category = 'Very Dark';
      confidence = 0.9;
    }

    return {
      category,
      confidence,
      color: SKIN_TONE_COLORS[category] ?? SKIN_TONE_COLORS['Unknown'],
    };
  }

  /** Convert RGB (0–255) to CIE Lab */
  private rgbToLab(
    r: number,
    g: number,
    b: number
  ): { L: number; a: number; b: number } {
    r /= 255;
    g /= 255;
    b /= 255;

    let x = r * 0.4124 + g * 0.3576 + b * 0.1805;
    let y = r * 0.2126 + g * 0.7152 + b * 0.0722;
    let z = r * 0.0193 + g * 0.1192 + b * 0.9505;

    x /= 0.95047;
    y /= 1.0;
    z /= 1.08883;

    const L = 116 * this.labF(y) - 16;
    const a = 500 * (this.labF(x) - this.labF(y));
    const bVal = 200 * (this.labF(y) - this.labF(z));

    return { L, a, b: bVal };
  }

  private labF(t: number): number {
    return t > 0.008856 ? Math.pow(t, 1 / 3) : 7.787 * t + 16 / 116;
  }

  /**
   * Combine the three analysis results.
   *
   * 'Unknown' votes are excluded whenever at least one method returned a
   * real category, so two Unknown results can no longer outvote a valid Lab
   * result. Ties are broken by the highest average confidence.
   */
  private combineAnalysisResults(results: SkinToneResult[]): SkinToneResult {
    const validResults = results.filter((r) => r.category !== 'Unknown');
    const candidates = validResults.length > 0 ? validResults : results;

    const categoryCounts: Record<string, number> = {};
    const categoryConfidences: Record<string, number[]> = {};

    for (const result of candidates) {
      if (!categoryCounts[result.category]) {
        categoryCounts[result.category] = 0;
        categoryConfidences[result.category] = [];
      }
      categoryCounts[result.category]++;
      categoryConfidences[result.category]!.push(result.confidence);
    }

    let bestCategory = 'Unknown';
    let maxCount = 0;
    let bestAvgConf = 0;

    for (const [category, count] of Object.entries(categoryCounts)) {
      const confs = categoryConfidences[category]!;
      const avgConf = confs.reduce((a, b) => a + b, 0) / confs.length;
      if (count > maxCount || (count === maxCount && avgConf > bestAvgConf)) {
        maxCount = count;
        bestCategory = category;
        bestAvgConf = avgConf;
      }
    }

    const winnerConfs = categoryConfidences[bestCategory] ?? [0];
    const avgConfidence =
      winnerConfs.reduce((a, b) => a + b, 0) / winnerConfs.length;

    return {
      category: bestCategory,
      confidence: Math.min(avgConfidence * 1.15, 0.95),
      color: SKIN_TONE_COLORS[bestCategory] ?? SKIN_TONE_COLORS['Unknown'],
      undertone: this.determineUndertone(results),
      analysisPoints: results.length,
    };
  }

  /**
   * Determine skin undertone from the set of analysis results.
   */
  private determineUndertone(results: SkinToneResult[]): SeasonUndertone {
    const categories = results.map((r) => r.category);

    if (categories.includes('Olive')) {
      return 'autumn';
    } else if (
      categories.some((cat) => ['Very Fair', 'Fair', 'Light'].includes(cat))
    ) {
      return 'summer';
    } else if (
      categories.some((cat) => ['Dark', 'Very Dark', 'Brown'].includes(cat))
    ) {
      return 'winter';
    } else {
      return 'spring';
    }
  }

  private getDefaultSkinToneResult(): SkinToneResult {
    return {
      category: 'Unknown',
      confidence: 0,
      color: SKIN_TONE_COLORS['Unknown'],
    };
  }
}

export const tensorFlowService = new TensorFlowService();
